import { useMemo, useState } from 'react';
import { Table, Button, Modal, Typography, Tag, Tooltip, message } from 'antd';
import { useParams } from 'react-router-dom';
import { useGetInspectionPlanByProjectQuery, useResetInspectionSampleMutation } from '../../../../redux/api/quality';
import { useGetLoggedInUserQuery } from '../../../../redux/api/users';
import { KeyenceCardContent } from '../../../../components/Operations/Quality/KeyenceCard';
import { getTagColor } from "../../../../utils/";
import { formatAttribution } from "../../../../utils/handCompletion";
import { getHandDisplayStatus, getHandTagColorStatus } from "../../../../utils/handDisplayStatus";
import { filterSamplesForBatch } from "../../../../utils/inspectionProjectId";
import { canResetInspectionSample } from '../../../../utils/canResetInspectionSample';
import InspectionResultsEntryPage from '../../../../pages/Operations/QualityControl/Manual';
import { InspectionResult, KeyenceReport, SampleViewItem } from '../../../../interfaces/Quality.Interface';
import { WarningOutlined } from '@ant-design/icons';

const { Title } = Typography

interface SampleMatrixRow {
  key: string;
  sample_id: string;
  label: number;
  hand_quality_status: string;
  hand_completed_by?: string | null;
  hand_completed_at?: string | null;
  from_sample?: InspectionResult[];
  status: string;
  [key: string]: any;
}

/** Attribution next to a status tag. With more than one measurement column
 *  the full "by <user> on <date>" makes the table overflow tablet widths, so
 *  compact mode ellipsizes it and moves the full text to a tooltip. */
const renderAttribution = (info: string | null, compact: boolean) => {
  if (!info) return null;
  if (!compact) {
    return (
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {info}
      </Typography.Text>
    );
  }
  return (
    <Tooltip title={info}>
      <Typography.Text
        type="secondary"
        style={{
          fontSize: 12,
          maxWidth: 120,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'inline-block',
          verticalAlign: 'middle',
        }}
      >
        {info}
      </Typography.Text>
    </Tooltip>
  );
};

const SampleMatrixTable = ({ projectId, resetDisabled, orderLineItemId }: { projectId?: string, resetDisabled?: boolean, orderLineItemId?: string }) => {
  const params = useParams<{ id: string }>();
  const effectiveProjectId = projectId ?? params.id;

  const [modalData, setModalData] = useState<boolean>(false);
  const [passedSample, setPassedSample] = useState<SampleViewItem | undefined>(undefined);
  const [modalHandSampleId, setModalHandSampleId] = useState<string | null>(null);
  const [handModalOpened, setHandModalOpened] = useState(false);
  const [modalResetSample, setModalResetSample] = useState<SampleMatrixRow | null>(null);

  const {
    data: planData,
    isLoading: isLoading,
    refetch
  } = useGetInspectionPlanByProjectQuery({ projectId: effectiveProjectId!, samples: 'full', project_mode: 'condensed' },
    { skip: !effectiveProjectId, refetchOnMountOrArgChange: true });

  const [resetSample, { isLoading: isResetting }] =
    useResetInspectionSampleMutation();
  const { data: loggedInUser } = useGetLoggedInUserQuery();
  const knownDenied =
    loggedInUser != null && !canResetInspectionSample(loggedInUser);
  const resetButtonDisabled = !!resetDisabled || knownDenied;
  const resetDeniedReason = knownDenied
    ? 'Only a Quality Control Lead, Shop Manager, or superuser can reset measurements.'
    : undefined;

  const views = planData?.views || [];
  const sampleViewList = planData?.sample_view_list || [];
  const samples = filterSamplesForBatch(planData?.samples || [], orderLineItemId);

  const hasHandColumn = useMemo(() => {
    return samples.some(item => item.hand_quality_status && item.hand_quality_status !== 'Not Applicable');
  }, [samples]);

  const rows = useMemo(() => {
    const grouped: Record<string, SampleMatrixRow> = {};

    samples.forEach(sample => {
      grouped[sample.id] = {
        key: sample.id,
        sample_id: sample.id,
        label: sample.sample_number,
        hand_quality_status: sample.hand_quality_status,
        hand_completed_by: sample.hand_completed_by,
        hand_completed_at: sample.hand_completed_at,
        from_sample: sample.from_sample,
        status: sample.status,
      };
    });

    sampleViewList.forEach(viewItem => {
      if (grouped[viewItem.sample_id]) {
        const key = viewItem.device_name ? `${viewItem.view}-${viewItem.device_name}` : viewItem.view;
        grouped[viewItem.sample_id][key] = viewItem.report;
      }
    });

    return Object.values(grouped).sort((a, b) => {
      if (a.label < b.label) return -1;
      if (a.label > b.label) return 1;
      return 0;
    });
  }, [samples, sampleViewList]);

  const columns = useMemo(() => {
    const baseCols = [
      {
        title: 'Sample',
        dataIndex: 'label',
        key: 'label',
        fixed: 'left' as const,
      }
    ];

    const viewDeviceList = planData?.view_devices && planData.view_devices.length > 0
      ? planData.view_devices
      : views.map(v => ({ view_name: v, device_name: null }));

    // More than one measurement column -> trim attributions, full text on hover.
    const compactAttribution = viewDeviceList.length > 1;

    const dynamicCols = viewDeviceList.map(item => {
      const view = item.view_name;
      const deviceName = item.device_name;
      const title = deviceName ? `${view} (${deviceName})` : view;
      const dataIndex = deviceName ? `${view}-${deviceName}` : view;

      return {
        title: title,
        dataIndex: dataIndex,
        key: dataIndex,
        render: (report: KeyenceReport | null, record: SampleMatrixRow) => {
          const status = report?.linked ? 'Completed' : 'Not Measured';
          const label = report?.linked ? 'Completed' : 'Not Measured';

          const linkedInfo = report?.linked
            ? formatAttribution(report.linked_by, report.linked_at)
            : null;

          return (
            <span style={{ whiteSpace: 'nowrap' }}>
              <Tag
                color={getTagColor(status)}
                onClick={() => {
                  const sampleView = sampleViewList.find(
                    (viewItem) => viewItem.sample_id === record.sample_id && viewItem.view === view && viewItem.device_name === deviceName
                  );
                  if (sampleView) {
                    setPassedSample(sampleView);
                    setModalData(true);
                  }
                }}
              >
                {label}
              </Tag>
              {renderAttribution(linkedInfo, compactAttribution)}
            </span>
          );
        },
      };
    });

    const handCol = hasHandColumn
      ? [
        {
          title: 'Hand',
          dataIndex: 'hand_quality_status',
          key: 'hand_quality_status',

          render: (status: string, record: SampleMatrixRow) => {
            const displayStatus = getHandDisplayStatus(status, record.from_sample);

            const completionInfo = status === 'Completed'
              ? formatAttribution(record.hand_completed_by, record.hand_completed_at)
              : null;

            return (
              <span style={{ whiteSpace: 'nowrap' }}>
                <Tag
                  color={getTagColor(getHandTagColorStatus(displayStatus))}
                  onClick={() => setModalHandSampleId(record.sample_id)}
                >
                  {displayStatus}
                </Tag>
                {renderAttribution(completionInfo, compactAttribution)}
              </span>
            );
          }
        },
      ]
      : [];

    const actionsCol = [
      {
        title: 'Actions',
        key: 'actions',
        render: (record: SampleMatrixRow) => {
          const button = (
            <Button
              size="small"
              icon={<WarningOutlined style={{ color: '#e8c83cff' }} />}
              onClick={() => {
                setModalResetSample(record)
              }}
              disabled={resetButtonDisabled}
            >
              Reset Measurements
            </Button>
          );
          return resetDeniedReason ? (
            <Tooltip title={resetDeniedReason}>
              <span>{button}</span>
            </Tooltip>
          ) : (
            button
          );
        }
      },
    ]
    return [...baseCols, ...dynamicCols, ...handCol, ...actionsCol];
  }, [views, hasHandColumn, planData?.view_devices, sampleViewList, resetButtonDisabled, resetDeniedReason]);

  const handleConfirmReset = async () => {
    if (!modalResetSample?.sample_id) return;
    try {
      await resetSample({ id: modalResetSample.sample_id }).unwrap();
      message.success(`Measurements reset for sample ${modalResetSample.label}`);
      setModalResetSample(null);
      refetch()
    } catch (err: any) {
      message.error(
        err?.data?.detail ||
        err?.message ||
        `Failed to reset sample ${modalResetSample.label}`
      );
    }
  };
  return (
    <div>
      <Title level={5}> Samples </Title>
      <div style={{ marginTop: 24 }}>
        <Table
          columns={columns}
          dataSource={rows}
          loading={isLoading}
          pagination={false}
          scroll={{ x: 'max-content' }}
          rowClassName={(record) =>
            record.status?.toLowerCase() === 'completed' ? 'row-completed' : ''
          }
        />
      </div>

      <Modal
        open={!!modalData}
        onCancel={() => setModalData(false)}
        afterClose={() => refetch()}
        footer={null}
        centered
        title="Report Details"
        width={800}
      >
        {planData && <KeyenceCardContent plan={planData} passedSample={passedSample} lockSample={true} showResults />}
      </Modal>
      <Modal
        open={!!modalHandSampleId}
        onCancel={() => setModalHandSampleId(null)}
        footer={null}
        afterClose={() => refetch()}
        afterOpenChange={setHandModalOpened}
        destroyOnClose
        centered
        width="80vw"
        // Fixed height: the hand-check content fills it and scrolls its own panels.
        styles={{ body: { height: '85vh', overflow: 'hidden' } }}
      >
        {/* react-range positions thumbs from the track's on-screen width, so mounting
            mid zoom-in animation (cached data) leaves every thumb shifted left. */}
        {modalHandSampleId && handModalOpened && (
          <InspectionResultsEntryPage
            projectId={planData?.project.id}
            sampleId={modalHandSampleId}
            isInModal={true}
          />
        )}
      </Modal>
      <Modal
        title="Resetting measurements confirmation"
        okText="Reset"
        okButtonProps={{ danger: true }}
        open={!!modalResetSample}
        onCancel={() => setModalResetSample(null)}
        onOk={handleConfirmReset}
        confirmLoading={isResetting}
        destroyOnClose
      >
        <p>Are you sure you want to reset the measurements for sample {" "}
          <strong>{modalResetSample?.label}</strong>?
        </p>
        <p>The measurements will be deleted.</p>
      </Modal>

    </div>
  );
};

export default SampleMatrixTable;
